import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { get, trim } from 'lodash';
import {
	SdfCard,
	SdfBox,
	SdfAvatar,
	SfcShellAppBarIcon,
	SdfIcon,
	SdfButton,
	SdfAlert,
	SdfFocusPane,
	SdfTextarea,
	SdfBusyIndicator,
} from '@synerg/react-components';
import { Controller, useForm } from 'react-hook-form';
import { SHELL_NAV_ICON } from '../../constants';
import './helpIcon.scss';
import { descriptionLength } from '../../common';

export default function HelpIcon() {
	const [showHelpMenu, setShowHelpMenu] = useState(false);
	const [contacts, setContacts] = useState([]);
	const [open, setOpen] = useState(false);
	const [toastShown, setToastShown] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const [alertMsg, setAlertMsg] = useState(null);
	const [selectedItem, setSelectedItem] = useState({});

	const [messageLength, setMessageLength] = useState(0);
	const [numberOfEndLineCharacter, setNumberOfEndLineCharacter] = useState(0);

	const { control, handleSubmit, reset } = useForm({
		message: '',
	});

	const menuRef = useRef(null);
	const helpIconRef = useRef(null);

	useEffect(() => {
		fetchContacts();
		document.body.addEventListener("click", onClickOutside);
		return () => document.removeEventListener("click", onClickOutside);
	}, []);

	const fetchContacts = async () => {
		try {
			// was /api/my-contacts
			const result = await axios.get(
				`${import.meta.env.VITE_ROOT_PATH}/api/contacts`
			);
			setContacts(result.data);
		} catch (e) {
			/* eslint-disable no-unused-vars */
			const errorMessage = get(
				e,
				"response.data.message",
				"Something went wrong when trying to get contacts. Please try again."
			);
			/* eslint-disable no-unused-vars */
		}
	};

	const onClickOutside = (e) => {
		const element = e.target;
		if (helpIconRef.current && helpIconRef.current.contains(element)) {
			return;
		}
		if (menuRef.current && !menuRef.current.contains(element)) {
			return setShowHelpMenu(false);
		}
	};

	const validate = {
		inValidCharacters: value => !(/[^A-Za-z0-9-. ]/.test(trim(value))) || 'Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), and spaces',
	};

	const updateMessageLength = (value) => {
		const { endLineChar, lengthWithoutEndlineChar } = descriptionLength(value);
		setNumberOfEndLineCharacter(endLineChar);
		setMessageLength(lengthWithoutEndlineChar);
	};

	const openModal = (item = {}) => {
		setSelectedItem(item);
		setShowHelpMenu(false);
		if (item.position === "technical") {
			window.open(
				"https://partnersupport.adp.com/hc/en-us/requests/new",
				"_blank"
			);
		} else {
			setOpen(true);
		}
	};

	const closeModal = () => {
		setOpen(false);
		reset({message: ''});
		setToastShown(false);
		setNumberOfEndLineCharacter(0);
		setMessageLength(0);
	};

	const getPositionMessage = (position) => {
		return {
			business: "business development advisor",
			technical: "technical advisor",
			partner: "partner development advisor",
		}[position];
	};

	const getTeam = (position) => {
		return {
			business: "business development team",
			technical: "technical team",
			partner: "partner development team",
		}[position];
	};

	const getProfileImage = (item = {}) => {
		return item.hasIcon ? `/self-service/api/contacts/${item.id}/image` : null;
	};

	const getInitials = (item = {}) => {
		let initials = "";
		const nameFirst = item.name.split(" ").map((s) => s[0]);

		if (nameFirst.length > 2) {
			initials = nameFirst.slice(0, 2).join("");
		} else {
			initials = nameFirst.slice(0, 1).join("");
		}
		return initials;
	};

	const ShowError = ({ message }) => {
		return (
		<SdfAlert slot="alert" size="sm" status="error" type="inline">
			{message}
		</SdfAlert>
		);
	};

	async function sendMessage(formData) {
		if (trim(formData.message).length === 0) {
			setError(true);
			setToastShown(true);
			setAlertMsg('Valid characters permitted are A-Z, a-z, 0-9, period (.), dash (-), and spaces');
			return;
		}
		setError(false);
		setToastShown(false);
		setLoading(true);
		try {
			await axios.post(`${import.meta.env.VITE_ROOT_PATH}/api/contact`, {
				id: selectedItem.id,
				...formData,
				position: selectedItem.position,
			});
			setLoading(false);
			setToastShown(true);
			setAlertMsg('Thank you, Your message is saved');
			closeModal();
		} catch (e) {
			setError(true);
			setAlertMsg('Something went wrong, Try again!');
			setLoading(false);
			setToastShown(true);
		}
	}

	return (
		<>
			<SfcShellAppBarIcon
				slot="icons"
				icon="action-help"
				ref={helpIconRef}
				className={`${SHELL_NAV_ICON} md:mr-4`}
				/* eslint-disable-next-line */
				href="javascript:void(0)"
				onClick={() => {
					setShowHelpMenu(!showHelpMenu);
				}}
			/>

			<div
				ref={menuRef}
				className={showHelpMenu ? "" : "hidden"}
				style={{ position: "fixed", top: "4rem", right: "5rem" }}
			>
				<SdfCard>
					<div className="inline-flex">
						<div className="text-heading-05 font-bold color-default flex-1">
							Need help?
						</div>
						<div className="">
							<SdfIcon
								icon="action-close"
								className="items-center cursor-pointer close-icon"
								onClick={() => setShowHelpMenu(false)}
							/>
						</div>
					</div>

					<div className="grid overflow-auto max-h-3xl p-2">
						{contacts.map((item, index) => {
							const image = item.hasIcon
								? `/self-service/api/contacts/${item.id}/image`
								: null;

							let initials = "";
							const nameFirst = item.name.split(" ").map((s) => s[0]);

							if (nameFirst.length > 2) {
								initials = nameFirst.slice(0, 2).join("");
							} else {
								initials = nameFirst.slice(0, 1).join("");
							}

							return (
								<div key={index} className="flex flex-row col-span-1">
									<SdfBox
										data-cvs-id="3"
										id={item.name + index}
										variant={index+ 1 === contacts.length ? 'clear' : 'underlined'}
										spacing="none"
										className="w-full pb-3 pt-3"
									>
										{/* first section */}
										<div className="inline-flex w-full">
											<div className="pr-4 pb-4">
												<SdfAvatar
													src={image}
													size={"lg"}
													shape={"circle"}
													initials={initials}
												/>
											</div>
											<div className="flex-1">
												<div className="color-default text-heading-03 font-bold capitalize">
													{item.name}
												</div>
												<div className="color-default text-body-02">
													{item.title}
												</div>
												<div className="mt-2 color-default">
													<SdfButton
														className="w-full"
														action="standard"
														emphasis={"tertiary"}
														size="sm"
														variant={"text"}
														onClick={() => openModal(item)}
													>
														{item.rhetorical}
													</SdfButton>
												</div>
											</div>
											<div className="pl-5 flex items-center">
												<SdfIcon
													icon="nav-page-next"
													className="items-center cursor-pointer close-icon"
													onClick={() => openModal(item)}
												/>
											</div>
										</div>
									</SdfBox>
								</div>
							);
						})}
					</div>
				</SdfCard>
			</div>

			<div className="color-default">
				{open ? (
					<SdfFocusPane
						visible={open}
						closeable={true}
						dismissOnClickOutside={true}
						heading={selectedItem.rhetorical}
						hideDismissButton={true}
						hideAcceptButton={true}
						onSdfDismiss={() => {
							closeModal();
						}}
						spacing={"loose"}
						id="sdf-modal"
						size="md"
					>
						{loading ? (
						<SdfBusyIndicator
							style={{ color: "#1a8099" }}
							size="xl"
							fullScreen={true}
							overlay={true}
						/>
						) : null}

						<SdfAlert
							hidden={!toastShown}
							autoClose={true}
							autoCloseAfter={3000}
							closeable={true}
							useAnimation={true}
							status={error ? "error" : "success"}
						>
							{alertMsg}
						</SdfAlert>

						<div className="flex flex-row mt-6 default-text pr-1 pl-1">
							<div className="pr-4 pb-4">
								<SdfAvatar
									src={getProfileImage(selectedItem)}
									size={"xl"}
									shape={"circle"}
									initials={getInitials(selectedItem)}
								/>
							</div>
							<div className="pb-4 pl-4">
								<div className="text-heading-03 mb-5">
									Send your{" "}
									<b>
										{selectedItem.name+ ", " +getPositionMessage(selectedItem.position)},
									</b> a message and a member of our {getTeam(selectedItem.position) + " "}
									will reply to you as soon as possible.
								</div>
								<div className="">
									<Controller
										name="message"
										control={control}
										rules={{
											validate: validate.inValidCharacters
										}}
										render={({
											field: { value, onChange },
											fieldState: { error },
										}) => (
											<SdfTextarea
												name="message"
												label="Message"
												rows={6}
												onSdfInput={event => { onChange(event.detail); updateMessageLength(event.detail) }}
												id="sdf-message"
												value={value}
												maxlength={256 + numberOfEndLineCharacter}
												state={error ? 'error' : 'normal'}
												resize="none"
											>
												{error && error.message &&
													<ShowError message={error.message} />
												}
											</SdfTextarea>
										)}
									/>
									<div className='mt-2 text-right'>{messageLength}/256</div>
								</div>
							</div>
						</div>

						<SdfButton slot="custom-buttons" onClick={closeModal}>Cancel</SdfButton>
        				<SdfButton
							slot="custom-buttons"
							emphasis="primary"
							disabled={messageLength >= 3 ? undefined : true}
							onClick={e => {
								handleSubmit(data => sendMessage(data))();
							}}
						>
							Save
						</SdfButton>
					</SdfFocusPane>
				) : null}
			</div>
		</>
	);
}
